import { useState } from'react';
import { motion, AnimatePresence } from'framer-motion';
import { chayot } from'../../data/frameworks';

// Extended creature data with visual properties
const extendedChayot = chayot.map((creature, index) => ({
 ...creature,
 colors: [
 { primary:'#EA580C', secondary:'#FBBF24' }, // Lion - Fire
 { primary:'#22C55E', secondary:'#84CC16' }, // Ox - Earth
 { primary:'#3B82F6', secondary:'#60A5FA' }, // Eagle - Air
 { primary:'#06B6D4', secondary:'#22D3EE' }, // Human - Water
 ][index],
 wings: 4,
 soundDescription: index === 0 ?'Like rushing waters' : index === 1 ?'Like thunder' : index === 2 ?'Like the voice of the Almighty' :'Like the tumult of an army',
}));

export default function Step2() {
 const [selectedCreature, setSelectedCreature] = useState<number | null>(null);

 const creaturePositions = [
 { direction:'East', description:'Human - Front' },
 { direction:'South', description:'Lion - Right' },
 { direction:'West', description:'Eagle - Back' },
 { direction:'North', description:'Ox - Left' },
 ];

 // Reorder to match positions: Human, Lion, Eagle, Ox
 const orderedChayot = [extendedChayot[3], extendedChayot[0], extendedChayot[2], extendedChayot[1]];

 return (
 <div className="section-container">
 {/* Introduction */}
 <div className="max-w-4xl mx-auto mb-16">
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 className="text-center mb-12"
 >
 <p className="hebrew-text text-2xl md:text-3xl text-primary-600 dark:text-primary-400 mb-4">
 ודמות פניהם פני אדם ופני אריה אל הימין
 </p>
 <p className="text-lg text-[var(--color-muted)] italic">
"As for the likeness of their faces, they had the face of a man,
 and the face of a lion on the right side..."
 </p>
 <p className="text-sm text-[var(--color-muted)] mt-2">- Ezekiel 1:10</p>
 </motion.div>

 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.2 }}
 className="prose prose-lg dark:prose-invert mx-auto"
 >
 <p>
 At the center of Ezekiel's vision are the four living creatures—the Chayot HaKodesh
 (Holy Living Beings). These magnificent beings bear the divine throne and represent
 the fundamental forces through which God manifests in creation.
 </p>
 </motion.div>
 </div>

 {/* Four Creatures Display */}
 <motion.div
 initial={{ opacity: 0, scale: 0.9 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ delay: 0.3 }}
 className="mb-16"
 >
 <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-4">
 The Four <span className="text-flame">Living Creatures</span>
 </h2>
 <p className="text-center text-[var(--color-muted)] mb-8 max-w-xl mx-auto">
 Click on each creature to explore its symbolism and meaning
 </p>

 {/* Creature Cards in a circle-like layout */}
 <div className="max-w-3xl mx-auto">
 {/* Central Kavod indicator */}
 <div className="flex justify-center mb-8">
 <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 flex items-center justify-center shadow-lg">
 <div className="text-center">
 <p className="hebrew-text text-xl text-white">כבוד</p>
 <p className="text-xs text-white/80">Kavod</p>
 </div>
 </div>
 </div>

 {/* Four creatures in a grid */}
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
 {orderedChayot.map((creature, index) => (
 <motion.button
 key={creature.name}
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.4 + index * 0.1 }}
 onClick={() => setSelectedCreature(selectedCreature === index ? null : index)}
 className={`p-6 rounded-2xl text-left transition-all duration-300 hover:shadow-lg ${
 selectedCreature === index
 ?'ring-2 ring-primary-500 shadow-lg'
 :'bg-[var(--color-card)] border border-[var(--color-border)] hover:border-primary-500/50'
 }`}
 style={{
 background: selectedCreature === index
 ? `linear-gradient(135deg, ${creature.colors.primary}15, ${creature.colors.secondary}15)`
 : undefined,
 }}
 >
 <div className="flex items-center gap-4">
 <div
 className="w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300"
 style={{
 background: `linear-gradient(135deg, ${creature.colors.primary}, ${creature.colors.secondary})`,
 }}
 >
 <span className="hebrew-text text-2xl text-white">{creature.hebrewName}</span>
 </div>
 <div>
 <h3 className="text-xl font-semibold">{creature.name}</h3>
 <p className="text-sm text-[var(--color-muted)]">
 {creaturePositions[index].direction} - {creature.element}
 </p>
 </div>
 </div>

 <p className="mt-4 text-[var(--color-muted)] text-sm">{creature.description}</p>

 <div className="mt-4 flex items-center gap-2">
 <span
 className="inline-block px-2 py-1 rounded text-xs font-medium"
 style={{
 backgroundColor: `${creature.colors.primary}20`,
 color: creature.colors.primary,
 }}
 >
 {creature.face}
 </span>
 <span
 className="inline-block px-2 py-1 rounded text-xs font-medium"
 style={{
 backgroundColor: `${creature.colors.secondary}20`,
 color: creature.colors.secondary,
 }}
 >
 4 Wings
 </span>
 </div>
 </motion.button>
 ))}
 </div>
 </div>
 </motion.div>

 {/* Selected Creature Details */}
 <AnimatePresence mode="wait">
 {selectedCreature !== null && (
 <motion.div
 key={selectedCreature}
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 exit={{ opacity: 0, y: -20 }}
 className="max-w-3xl mx-auto mb-16"
 >
 <div
 className="card relative overflow-hidden transition-all duration-300"
 style={{
 borderColor: orderedChayot[selectedCreature].colors.primary,
 borderWidth:'2px',
 }}
 >
 {/* Background gradient */}
 <div
 className="absolute inset-0 opacity-10"
 style={{
 background: `linear-gradient(135deg, ${orderedChayot[selectedCreature].colors.primary}, ${orderedChayot[selectedCreature].colors.secondary})`,
 }}
 />

 <div className="relative z-10">
 <div className="flex items-center justify-between mb-6">
 <div>
 <h3 className="text-3xl font-semibold">{orderedChayot[selectedCreature].name}</h3>
 <p
 className="hebrew-text text-2xl"
 style={{ color: orderedChayot[selectedCreature].colors.primary }}
 >
 {orderedChayot[selectedCreature].hebrewName}
 </p>
 </div>
 <div className="text-right text-sm text-[var(--color-muted)]">
 <p><span className="font-semibold">Face:</span> {orderedChayot[selectedCreature].face}</p>
 <p><span className="font-semibold">Direction:</span> {orderedChayot[selectedCreature].direction}</p>
 </div>
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
 <div
 className="p-4 rounded-lg transition-all duration-300"
 style={{ backgroundColor: `${orderedChayot[selectedCreature].colors.primary}20` }}
 >
 <p className="text-sm text-[var(--color-muted)]">Element</p>
 <p className="font-semibold text-lg">{orderedChayot[selectedCreature].element}</p>
 </div>
 <div
 className="p-4 rounded-lg transition-all duration-300"
 style={{ backgroundColor: `${orderedChayot[selectedCreature].colors.secondary}20` }}
 >
 <p className="text-sm text-[var(--color-muted)]">Wings</p>
 <p className="font-semibold text-lg">{orderedChayot[selectedCreature].wings}</p>
 </div>
 <div className="p-4 rounded-lg bg-primary-600/10 col-span-2 transition-all duration-300">
 <p className="text-sm text-[var(--color-muted)]">Sefirah Attribute</p>
 <p className="font-semibold">{orderedChayot[selectedCreature].attribute}</p>
 </div>
 </div>

 <p className="text-[var(--color-muted)] mb-4">
 {orderedChayot[selectedCreature].description}
 </p>

 <div className="p-4 rounded-lg bg-secondary-700/10 border border-secondary-700/20 transition-all duration-300">
 <p className="text-sm font-semibold mb-1">Sound of Their Wings:</p>
 <p className="text-[var(--color-muted)] italic">
"{orderedChayot[selectedCreature].soundDescription}"
 </p>
 </div>

 <button
 onClick={() => setSelectedCreature(null)}
 className="mt-4 text-sm text-[var(--color-muted)] hover:text-[var(--color-foreground)] transition-all duration-300"
 >
 Close details
 </button>
 </div>
 </div>
 </motion.div>
 )}
 </AnimatePresence>

 {/* All Creatures Grid */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.6 }}
 >
 <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">
 Understanding the <span className="text-[#202122] font-bold font-serif">Chayot</span>
 </h2>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 {chayot.map((creature, index) => (
 <motion.div
 key={creature.name}
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.7 + index * 0.1 }}
 className="card group transition-all duration-300 hover:border-primary-600/50 hover:shadow-lg"
 >
 <div className="flex items-center gap-4 mb-4">
 <div
 className="w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
 style={{
 background: `linear-gradient(135deg, ${extendedChayot[index].colors.primary}40, ${extendedChayot[index].colors.secondary}40)`,
 }}
 >
 <span className="hebrew-text text-2xl">{creature.hebrewName}</span>
 </div>
 <div>
 <h3 className="text-xl font-semibold">{creature.name}</h3>
 <p className="text-sm text-[var(--color-muted)]">
 {creature.direction} - {creature.element}
 </p>
 </div>
 </div>

 <p className="text-[var(--color-muted)] mb-4">{creature.description}</p>

 <div
 className="p-3 rounded-lg transition-all duration-300"
 style={{
 background: `linear-gradient(135deg, ${extendedChayot[index].colors.primary}10, ${extendedChayot[index].colors.secondary}10)`,
 }}
 >
 <p className="text-sm">
 <span className="font-semibold">Sefirah: </span>
 <span style={{ color: extendedChayot[index].colors.primary }}>
 {creature.attribute}
 </span>
 </p>
 </div>
 </motion.div>
 ))}
 </div>
 </motion.div>

 {/* Physical Description */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.9 }}
 className="mt-16 p-8 rounded-2xl bg-[var(--color-card)] border border-[var(--color-border)]"
 >
 <h3 className="text-xl font-semibold mb-6 text-center">
 Physical Description from Ezekiel 1:5-14
 </h3>

 <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-[var(--color-muted)]">
 <div>
 <h4 className="font-semibold text-[var(--color-foreground)] mb-2">Form</h4>
 <ul className="space-y-2 text-sm">
 <li className="flex items-center gap-2">
 <span className="w-2 h-2 rounded-full bg-primary-600" />
 Human-like form overall
 </li>
 <li className="flex items-center gap-2">
 <span className="w-2 h-2 rounded-full bg-primary-600" />
 Four faces each
 </li>
 <li className="flex items-center gap-2">
 <span className="w-2 h-2 rounded-full bg-primary-600" />
 Four wings each
 </li>
 <li className="flex items-center gap-2">
 <span className="w-2 h-2 rounded-full bg-primary-600" />
 Straight legs with calf-like feet
 </li>
 <li className="flex items-center gap-2">
 <span className="w-2 h-2 rounded-full bg-primary-600" />
 Gleaming like burnished bronze
 </li>
 </ul>
 </div>
 <div>
 <h4 className="font-semibold text-[var(--color-foreground)] mb-2">Movement</h4>
 <ul className="space-y-2 text-sm">
 <li className="flex items-center gap-2">
 <span className="w-2 h-2 rounded-full bg-secondary-600" />
 Moved in any direction without turning
 </li>
 <li className="flex items-center gap-2">
 <span className="w-2 h-2 rounded-full bg-secondary-600" />
 Swift as lightning
 </li>
 <li className="flex items-center gap-2">
 <span className="w-2 h-2 rounded-full bg-secondary-600" />
 Moved together as one
 </li>
 <li className="flex items-center gap-2">
 <span className="w-2 h-2 rounded-full bg-secondary-600" />
 Followed the spirit's direction
 </li>
 <li className="flex items-center gap-2">
 <span className="w-2 h-2 rounded-full bg-secondary-600" />
 Rose and descended with the wheels
 </li>
 </ul>
 </div>
 <div>
 <h4 className="font-semibold text-[var(--color-foreground)] mb-2">Wings</h4>
 <ul className="space-y-2 text-sm">
 <li className="flex items-center gap-2">
 <span className="w-2 h-2 rounded-full bg-[#3366cc]" />
 Two wings stretched upward, touching
 </li>
 <li className="flex items-center gap-2">
 <span className="w-2 h-2 rounded-full bg-[#3366cc]" />
 Two wings covering their bodies
 </li>
 <li className="flex items-center gap-2">
 <span className="w-2 h-2 rounded-full bg-[#3366cc]" />
 Sound like rushing waters
 </li>
 <li className="flex items-center gap-2">
 <span className="w-2 h-2 rounded-full bg-[#3366cc]" />
 Like the voice of the Almighty
 </li>
 <li className="flex items-center gap-2">
 <span className="w-2 h-2 rounded-full bg-[#3366cc]" />
 Human hands under wings
 </li>
 </ul>
 </div>
 </div>
 </motion.div>

 {/* Mathematical Structure */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 1 }}
 className="mt-12 p-8 rounded-2xl bg-gradient-to-r from-primary-600/10 to-secondary-700/10 border border-primary-600/20"
 >
 <h3 className="text-xl font-semibold mb-6 text-center">
 The Sacred Mathematics of the Chayot
 </h3>

 <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 text-center">
 <div className="transition-all duration-300 hover:scale-105">
 <div className="text-4xl font-bold text-flame mb-2">4</div>
 <p className="text-sm text-[var(--color-muted)]">Living Creatures</p>
 <p className="text-xs text-[var(--color-muted)] italic mt-1">Chayot HaKodesh</p>
 </div>
 <div className="transition-all duration-300 hover:scale-105">
 <div className="text-4xl font-bold text-[#202122] font-bold font-serif mb-2">4</div>
 <p className="text-sm text-[var(--color-muted)]">Faces Each</p>
 <p className="text-xs text-[var(--color-muted)] italic mt-1">16 Faces Total</p>
 </div>
 <div className="transition-all duration-300 hover:scale-105">
 <div className="text-4xl font-bold text-flame mb-2">4</div>
 <p className="text-sm text-[var(--color-muted)]">Wings Each</p>
 <p className="text-xs text-[var(--color-muted)] italic mt-1">16 Wings Total</p>
 </div>
 <div className="transition-all duration-300 hover:scale-105">
 <div className="text-4xl font-bold text-[#202122] font-bold font-serif mb-2">4</div>
 <p className="text-sm text-[var(--color-muted)]">Directions</p>
 <p className="text-xs text-[var(--color-muted)] italic mt-1">Encompassing All</p>
 </div>
 </div>

 <p className="text-center text-[var(--color-muted)] mt-6 text-sm">
 The number four represents completeness in creation - four directions, four elements, four seasons.
 The Chayot embody this cosmic completeness, bearing the throne of the One who transcends all.
 </p>
 </motion.div>
 </div>
 );
}
